/**
 * useMentionAutocomplete — composer `@`-mention autocomplete state + search.
 *
 * Mirrors the `useCommandAutocomplete` precedent, but owns MORE than a bare
 * state container: it also drives the debounced `hybridSearch` lookup, the
 * onChange detection (via `detectActiveMention`), the keyboard-nav + selection
 * (via `applyMention`), and the drag-drop apply. ALL of this lives here (and in
 * the leaf components) so ChatBox — pinned at its file-size ceiling — only
 * grows by a single hook call.
 *
 * The mention marker is the canonical `@[Title](type:id)` string embedded in the
 * plain `<textarea>` value (see `utils/mention-parser.ts`). A selection splices
 * that marker into the value and restores the caret; submit-time extraction
 * (`extractMentionReferences`) turns the markers into `context.mentionedObjects`
 * for the agent — that wiring lives in `useChatSubmit`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { api } from '@/lib/api';
import { useSession } from '@/features/auth/hooks/useSession';
import {
  detectActiveMention,
  applyMention,
  type ActiveMention,
  type MentionReference,
} from '@/features/chat/utils/mention-parser';
import type { SearchResultObject } from '@/lib/api/endpoints/search';

/** A mention candidate row. Keyed to `SearchResultObject` ({id,type,title}) so a
 *  hybridSearch result maps straight into a `MentionReference` on select. */
export interface MentionCandidate {
  id: string;
  type: string;
  title: string;
}

const SEARCH_DEBOUNCE_MS = 300;
const MAX_MENTION_RESULTS = 8;

export interface UseMentionAutocompleteDeps {
  inputValue: string;
  setInputValue: Dispatch<SetStateAction<string>>;
  chatInputRef: RefObject<HTMLTextAreaElement | null>;
  activeConversationId?: string;
}

export interface UseMentionAutocompleteReturn {
  /** Whether the mention dropdown should render. */
  showMentionDropdown: boolean;
  /** The current candidate rows (from hybridSearch). */
  mentionMatches: MentionCandidate[];
  /** Highlighted row for keyboard nav. */
  selectedMentionIndex: number;
  setSelectedMentionIndex: Dispatch<SetStateAction<number>>;
  /** True while a search request is in flight (dropdown shows a loading row). */
  isMentionLoading: boolean;
  /**
   * Per-keystroke detector. Call from the composer's onChange with the new value
   * and the caret (textarea.selectionStart). Opens/closes the dropdown and kicks
   * off a debounced search. Returns true when a mention is active (so the caller
   * can skip the slash-command branch).
   */
  handleMentionInput: (value: string, caret: number) => boolean;
  /** Apply the candidate at `index` (default = highlighted) into the textarea. */
  selectMention: (index?: number) => void;
  /** Apply a drag-dropped object reference at the current caret. */
  applyDroppedReference: (ref: MentionReference) => void;
  /** Close the dropdown without selecting (Escape / blur). */
  closeMentionDropdown: () => void;
}

export function useMentionAutocomplete(
  deps: UseMentionAutocompleteDeps,
): UseMentionAutocompleteReturn {
  const { inputValue, setInputValue, chatInputRef, activeConversationId } = deps;
  const { token } = useSession();

  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionMatches, setMentionMatches] = useState<MentionCandidate[]>([]);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [isMentionLoading, setIsMentionLoading] = useState(false);

  // The active `@token` span under the caret (for applyMention splicing).
  const activeMentionRef = useRef<ActiveMention | null>(null);
  // Debounce timer + a monotonically increasing request id so a stale response
  // from a slower earlier query can't clobber the latest results.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeqRef = useRef(0);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const closeMentionDropdown = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    requestSeqRef.current += 1; // invalidate any in-flight response
    activeMentionRef.current = null;
    setShowMentionDropdown(false);
    setMentionMatches([]);
    setSelectedMentionIndex(0);
    setIsMentionLoading(false);
  }, []);

  const runSearch = useCallback(
    (query: string) => {
      const seq = ++requestSeqRef.current;
      // No auth token yet (initial-load race / expired session): don't fire an
      // unauthenticated hybridSearch that 401s and leaves the dropdown spinning
      // forever. Drop to the empty state instead (issue #1457). Mirrors the
      // `enabled: Boolean(token)` gate used for authed TanStack queries.
      if (!token) {
        setMentionMatches([]);
        setIsMentionLoading(false);
        setShowMentionDropdown(activeMentionRef.current !== null);
        return;
      }
      setIsMentionLoading(true);
      api.search
        .hybridSearch(token, query, {
          conversationIds: activeConversationId ? [activeConversationId] : undefined,
        })
        .then((res) => {
          if (seq !== requestSeqRef.current) return; // stale — newer query won
          const rows: MentionCandidate[] = (res?.results ?? [])
            .map((item: { object: SearchResultObject }) => item.object)
            .filter((obj): obj is SearchResultObject => Boolean(obj?.id))
            .slice(0, MAX_MENTION_RESULTS)
            .map((obj) => ({ id: obj.id, type: obj.type, title: obj.title }));
          setMentionMatches(rows);
          setSelectedMentionIndex(0);
          setIsMentionLoading(false);
          // An active mention with zero hits still shows the dropdown (empty
          // state) so the user gets feedback instead of a silent no-op.
          setShowMentionDropdown(activeMentionRef.current !== null);
        })
        .catch(() => {
          if (seq !== requestSeqRef.current) return;
          setMentionMatches([]);
          setIsMentionLoading(false);
        });
    },
    [token, activeConversationId],
  );

  const handleMentionInput = useCallback(
    (value: string, caret: number): boolean => {
      const active = detectActiveMention(value, caret);
      if (!active) {
        if (showMentionDropdown || activeMentionRef.current) closeMentionDropdown();
        return false;
      }
      activeMentionRef.current = active;
      setShowMentionDropdown(true);
      // Debounce the network call; show the loading row immediately.
      setIsMentionLoading(true);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const query = active.query.trim();
      debounceRef.current = setTimeout(() => runSearch(query), SEARCH_DEBOUNCE_MS);
      return true;
    },
    [showMentionDropdown, closeMentionDropdown, runSearch],
  );

  /** Splice a marker into the textarea value and restore the caret after it. */
  const insertReference = useCallback(
    (active: ActiveMention, ref: MentionReference) => {
      const { text, caret } = applyMention(inputValue, active, ref);
      setInputValue(text);
      closeMentionDropdown();
      // Restore caret after React commits the new value.
      requestAnimationFrame(() => {
        const ta = chatInputRef.current;
        if (!ta) return;
        ta.focus();
        ta.selectionStart = caret;
        ta.selectionEnd = caret;
      });
    },
    [inputValue, setInputValue, chatInputRef, closeMentionDropdown],
  );

  const selectMention = useCallback(
    (index?: number) => {
      const active = activeMentionRef.current;
      if (!active) return;
      const pick = mentionMatches[index ?? selectedMentionIndex];
      if (!pick) return;
      insertReference(active, { id: pick.id, type: pick.type, title: pick.title });
    },
    [mentionMatches, selectedMentionIndex, insertReference],
  );

  const applyDroppedReference = useCallback(
    (ref: MentionReference) => {
      // Drop inserts at the live caret (or end of text). Build a zero-width
      // active span there so applyMention splices the marker in cleanly.
      const ta = chatInputRef.current;
      const caret = ta?.selectionStart ?? inputValue.length;
      insertReference({ query: '', start: caret, end: caret }, ref);
    },
    [chatInputRef, inputValue, insertReference],
  );

  return {
    showMentionDropdown,
    mentionMatches,
    selectedMentionIndex,
    setSelectedMentionIndex,
    isMentionLoading,
    handleMentionInput,
    selectMention,
    applyDroppedReference,
    closeMentionDropdown,
  };
}
